import {observer} from 'mobx-react-lite'
import React from 'react'
import useResizeObserver from 'use-resize-observer'
import {Lang} from 'yougile-lang'
import {ArrUtil, logger, Util} from 'yougile-util'
import {
  DataObjectId,
  GTDStage,
  isTaskWithoutPermissions,
  Subtask
} from 'yougile-model'
import {Sync} from '@client/sync'
import {Dash} from '@components/statistics/stats-reports/subtasks-in-report/dash'
import {checkOnStraight} from '@components/statistics/stats-reports/subtasks-in-report/subtasks-in-report'
import {Toggler} from '@components/statistics/stats-reports/subtasks-in-report/toggler'
import {SyncState} from '@components/subtasks-old/subtasks'
import {
  StoreContext,
  SubtasksStore,
  useStore
} from '@components/subtasks-old/subtasks-store'
import {useBoolean, useMemoOne} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {SubtaskUtil} from '@extensions/subtask-util'
import {SortKey, TaskPage} from './my-tasks-conf'
import {DragControl, MyTasksListItem} from './my-tasks-list-item'
import {TaskItem, taskItemFromHub} from './task-item'

const maxShownTasksStep = 20

let dragEnterFrame: number

const hasSubtasks = (taskId?: DataObjectId): boolean => {
  const box = Sync.getCurrentSandbox()
  const hub = box.getHub(taskId)
  return !!hub && SubtaskUtil.hasSubtasks(hub)
}

const mergeTaskItemWithSubtask = (
  taskItem: TaskItem,
  subtask: Subtask
): TaskItem => {
  return {
    ...taskItem,
    title: subtask.title
  }
}

const getTaskItem = (taskId: DataObjectId, subtask?: Subtask): TaskItem => {
  const box = Sync.getCurrentSandbox()
  const hub = box.getHub(taskId)
  return isTaskWithoutPermissions(hub) && subtask
    ? mergeTaskItemWithSubtask(taskItemFromHub(box, hub), subtask)
    : taskItemFromHub(box, hub)
}

const RenderBlock = (props: {
  taskId: DataObjectId
  subtask?: Subtask
  dragCtl: DragControl<DataObjectId>
  hasStraightLine: boolean[]
  itemsY: Subtask[]
  lineBefore: boolean
  draggable: boolean
  withAssigned: boolean
  listProps: MyTasksListProps
  isManualSort: boolean
  page: TaskPage
}) => {
  const box = Sync.getCurrentSandbox()
  const hub = box.getHub(props.taskId)
  const disabled = isTaskWithoutPermissions(hub)
  const bem = Util.bemFrom('my-tasks')
  const taskItem = getTaskItem(props.taskId, props.subtask)
  const {ref, height = 1} = useResizeObserver()
  const straightLineHeight = 40
  const count = height / straightLineHeight
  const numberOfStraightLines = count < 1 ? 2 : Math.round(count) + 1

  return (
    <>
      <div
        ref={ref}
        className={cn(
          'flex relative overflow-hidden',
          props.hasStraightLine.length === 0 && 'ml-24'
        )}
      >
        <Dash
          numberOfStraightLines={numberOfStraightLines}
          hasStraightLine={props.hasStraightLine}
          taskId={props.taskId}
          itemsY={props.itemsY}
          flex
          rightClassName={props.isManualSort ? 'mt-8' : 'mt-10'}
          mainClassName={props.isManualSort ? 'mt-2' : '-mt-2'}
        />
        {props.lineBefore && <div className={bem('starred-line')} />}
        <div className="flex-1">
          <MyTasksListItem
            disabled={disabled}
            task={taskItem}
            listProps={props.listProps}
            dragCtl={props.dragCtl}
            draggable={props.draggable}
            withAssigned={props.withAssigned}
            page={props.page}
          />
        </div>
      </div>
    </>
  )
}

// eslint-disable-next-line max-lines-per-function
const RenderSubtasksBlock = (props: {
  taskId: DataObjectId
  subtask?: Subtask
  dragCtl: DragControl<DataObjectId>
  hasStraightLine: boolean[]
  itemsY: Subtask[]
  lineBefore: boolean
  draggable: boolean
  withAssigned: boolean
  listProps: MyTasksListProps
  isManualSort: boolean
  page: TaskPage
}) => {
  const box = Sync.getCurrentSandbox()
  const hub = box.getHub(props.taskId)
  const disabled = isTaskWithoutPermissions(hub)
  const bem = Util.bemFrom('my-tasks')
  const open = useBoolean(false)
  const taskItem = getTaskItem(props.taskId, props.subtask)

  const store = useMemoOne(
    () => new SubtasksStore(props.taskId),
    [props.taskId]
  )
  const toggle = () => {
    open.toggle()
  }
  const {ref, height = 1} = useResizeObserver()
  const straightLineHeight = 40
  const count = height / straightLineHeight
  const numberOfStraightLines = count < 1 ? 2 : Math.round(count) + 1

  return (
    <StoreContext.Provider value={store}>
      <>
        <div className="relative flex overflow-hidden" ref={ref}>
          <Dash
            flex
            hasSubtasks
            hasStraightLine={props.hasStraightLine}
            taskId={props.taskId}
            itemsY={props.itemsY}
            numberOfStraightLines={numberOfStraightLines}
            rightClassName={props.isManualSort ? 'mt-8' : 'mt-10'}
            mainClassName={props.isManualSort ? 'mt-2' : '-mt-2'}
          />
          <Toggler
            flex
            arrowDown={open.value}
            onToggle={toggle}
            numberOfStraightLines={numberOfStraightLines}
            className={props.isManualSort ? 'mt-10' : 'mt-6'}
          />
          {props.lineBefore && <div className={bem('starred-line')} />}
          <div className="flex-1">
            <MyTasksListItem
              disabled={disabled}
              task={taskItem}
              listProps={props.listProps}
              dragCtl={props.dragCtl}
              draggable={props.draggable}
              withAssigned={props.withAssigned}
              page={props.page}
            />
          </div>
        </div>
      </>
      {open.value && (
        <SubtaskList
          dragCtl={props.dragCtl}
          hasStraightLine={props.hasStraightLine}
          draggable={false}
          withAssigned={props.withAssigned}
          listProps={props.listProps}
          lineBefore={false}
          isManualSort={props.isManualSort}
          page={props.page}
        />
      )}
      <SyncState />
    </StoreContext.Provider>
  )
}

const SubtaskList = observer(
  (props: {
    dragCtl: DragControl<DataObjectId>
    hasStraightLine: boolean[]
    draggable: boolean
    withAssigned: boolean
    listProps: MyTasksListProps
    lineBefore: boolean
    isManualSort: boolean
    page: TaskPage
  }) => {
    const [, store] = useStore()
    return (
      <>
        {store.subtasks.map((t, ix, {length}) => {
          if (hasSubtasks(t.id)) {
            return (
              <RenderSubtasksBlock
                key={t.id}
                taskId={t.id}
                subtask={t}
                dragCtl={props.dragCtl}
                hasStraightLine={[
                  ...props.hasStraightLine,
                  checkOnStraight(ix, length)
                ]}
                itemsY={store.subtasks}
                listProps={props.listProps}
                draggable={props.draggable}
                withAssigned={props.withAssigned}
                lineBefore={props.lineBefore}
                isManualSort={props.isManualSort}
                page={props.page}
              />
            )
          } else {
            return (
              <RenderBlock
                key={t.id}
                taskId={t.id}
                subtask={t}
                dragCtl={props.dragCtl}
                hasStraightLine={[...props.hasStraightLine, false]}
                itemsY={store.subtasks}
                listProps={props.listProps}
                draggable={props.draggable}
                withAssigned={props.withAssigned}
                lineBefore={props.lineBefore}
                isManualSort={props.isManualSort}
                page={props.page}
              />
            )
          }
        })}
      </>
    )
  }
)

export interface MyTasksListProps {
  title: React.ReactNode
  tasks: TaskItem[]
  sort: SortKey
  toggleCompleted: (task: TaskItem) => void
  setStage: (task: TaskItem, stage: GTDStage, reason: string) => void
  stage: GTDStage
  page: TaskPage
  draggedId: DataObjectId | undefined
  setDrag: (e: React.DragEvent, taskId: DataObjectId | undefined) => void
  itemClick: (task: TaskItem) => () => void
  setOrder(key: string, order: DataObjectId[]): void
  hideTitle?: boolean
  initialExpanded: boolean
}

// eslint-disable-next-line max-lines-per-function
const MyTasksListBase = (props: MyTasksListProps) => {
  const {
    tasks,
    title,
    draggedId,
    stage,
    hideTitle = false,
    page,
    sort,
    initialExpanded
  } = props
  const [maxShownTasks, setMaxShownTasks] = React.useState(maxShownTasksStep)
  const [expanded, setExpanded] = React.useState<boolean>(initialExpanded)
  const [dragOrder, setDragOrder] = React.useState(tasks.map(t => t.id))

  React.useEffect(() => {
    setDragOrder(tasks.map(t => t.id))
  }, [props.tasks])

  const toggle = () => {
    setMaxShownTasks(maxShownTasksStep)
    setExpanded(!expanded)
  }
  const showMore = () => {
    setMaxShownTasks(maxShownTasks + maxShownTasksStep)
  }

  const bem = Util.bemFrom('my-tasks')
  const taskIx = draggedId ? ArrUtil.createIndex(tasks, t => t.id) : undefined
  const tasksWithDragged = draggedId
    ? ArrUtil.unique(dragOrder, id => id)
        .map(id => taskIx?.get(id))
        .filter(a => !!a)
    : tasks

  const dragCtl: DragControl<DataObjectId> = {
    dragStart: id => e => {
      props.setDrag(e, id)
      e.dataTransfer.effectAllowed = 'copyMove'
    },
    dragEnd: (e: React.DragEvent) => {
      e.preventDefault()
      props.setDrag(e, undefined)
    },
    drop: (to: DataObjectId) => (e: React.DragEvent<HTMLElement>) => {
      if (!draggedId) {
        return
      }
      e.preventDefault()
      cancelAnimationFrame(dragEnterFrame)
      const draggedTask = taskIx?.get(draggedId)
      const targetTask = taskIx?.get(to)
      if (targetTask && draggedTask) {
        if (targetTask.starred !== draggedTask.starred) {
          logger.log('Skip enter')
          return
        }
      } else {
        logger.log('Not found')
      }

      const oldIx = dragOrder.indexOf(draggedId)
      const newIx = dragOrder.indexOf(to)

      const newOrder = [...dragOrder]
      newOrder.splice(oldIx, 1)
      newOrder.splice(newIx, 0, draggedId)
      props.setDrag(e, undefined)
      props.setOrder(page + '-tasks', newOrder)
    },
    dropToBeginning(id: DataObjectId) {
      if (!draggedId) {
        return
      }
      const oldIx = dragOrder.indexOf(id)
      const newOrder = [...dragOrder]
      newOrder.splice(oldIx, 1)
      newOrder.splice(0, 0, draggedId)
      props.setOrder(page + '-tasks', newOrder)
    },
    dragEnter: inside => e => {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      if (draggedId && inside !== draggedId) {
        const draggedTask = taskIx?.get(draggedId)
        const targetTask = taskIx?.get(inside)
        if (targetTask && draggedTask) {
          if (targetTask.starred !== draggedTask.starred) {
            logger.log('Skip enter')
            return
          }
        } else {
          logger.log('Not found')
        }

        const oldIx = dragOrder.indexOf(draggedId)
        const newIx = dragOrder.indexOf(inside)
        const newOrder = [...dragOrder]
        newOrder.splice(oldIx, 1)
        newOrder.splice(newIx, 0, draggedId)

        cancelAnimationFrame(dragEnterFrame)
        dragEnterFrame = requestAnimationFrame(() => {
          setDragOrder(newOrder)
        })
      }
    },
    dragLeave: _inside => _e => {
      //
    },
    dragOver: _inside => e => {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
    }
  }

  const draggable =
    (stage === 'current' || stage === 'new') && sort === 'manual'

  let starred = false
  return (
    <div
      className={bem('list', {
        expanded,
        [stage]: true,
        dragging: !!draggedId,
        notDragging: !draggedId,
        notDraggable: !draggable,
        draggable
      })}
    >
      {!hideTitle && (
        <div
          className={bem('list-title')}
          onClick={toggle}
          style={{zIndex: 100001}}
        >
          {title}
          <div className={bem('list-count')}>({tasks.length})</div>
        </div>
      )}

      {(expanded || hideTitle) && (
        <>
          <div className={cn('ml-24', bem('task-list'))}>
            {tasksWithDragged.slice(0, maxShownTasks).map(t => {
              if (!t) {
                return null
              }
              const lineBefore = draggable && starred && !t.starred
              starred = t.starred
              if (hasSubtasks(t.id)) {
                return (
                  <RenderSubtasksBlock
                    key={t.id}
                    taskId={t.id}
                    hasStraightLine={[]}
                    itemsY={[]}
                    listProps={props}
                    dragCtl={dragCtl}
                    draggable={draggable}
                    withAssigned={page !== 'my' && page !== 'private'}
                    lineBefore={lineBefore}
                    isManualSort={sort === 'manual'}
                    page={page}
                  />
                )
              } else {
                return (
                  <RenderBlock
                    key={t.id}
                    taskId={t.id}
                    hasStraightLine={[]}
                    itemsY={[]}
                    listProps={props}
                    dragCtl={dragCtl}
                    draggable={draggable}
                    withAssigned={page !== 'my' && page !== 'private'}
                    lineBefore={lineBefore}
                    isManualSort={sort === 'manual'}
                    page={page}
                  />
                )
              }
            })}
          </div>
          <div className={bem('list-actions')}>
            {tasks.length > maxShownTasks && (
              <div className={bem('more')}>
                {Lang.msg.moreTasks}{' '}
                <div className={bem('more-num')}>
                  {tasks.length - maxShownTasks}.
                </div>
                <div className={bem('more-btn')} onClick={showMore}>
                  {Lang.msg.showMore2}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export const MyTasksList = MyTasksListBase
